// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: fanArtSubmissions/$submissionId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Fan art submissions for Danger Dan! Users submit fan art with artist name and optional X/Twitter link. Admin/founder review and approve/reject. Approved submissions are publicly visible.
 */

export interface FanArtSubmissionsRequest {
  imageUrl: string;
  title?: string;
  description?: string;
  status: string;
  walletAddress?: string;
  submittedAt: number | TimeOperation | IncrementOperation | TokenAmount;
  reviewedAt?: number | TimeOperation | IncrementOperation | TokenAmount;
  artistName: string;
  xLink?: string;
  xProfile?: string;
}

export interface FanArtSubmissionsResponse {
  imageUrl: string;
  title?: string;
  description?: string;
  status: string;
  walletAddress?: string;
  submittedAt: number;
  reviewedAt?: number;
  artistName: string;
  xLink?: string;
  xProfile?: string;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a FanArtSubmissions operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildFanArtSubmissions(submissionId: string, data: FanArtSubmissionsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `fanArtSubmissions/${submissionId}`, document: doc };
}

/**
 * Anyone can create a submission without wallet authentication. artistName is required and immutable after creation. xLink is optional (a URL to the artist's X/Twitter post or profile) and immutable after creation. xProfile is optional (the artist's X/Twitter profile URL or handle, e.g. @username or https://x.com/username) and immutable after creation. imageUrl and submittedAt are also immutable after creation. status should be set to 'pending' on creation. No Twitter OAuth is involved. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setFanArtSubmissions(submissionId: string, data: FanArtSubmissionsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`fanArtSubmissions/${submissionId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting FanArtSubmissions: ${formatError(error)}`);
    return false;
  }
}

export type FanArtSubmissionsRequestUpdate = Partial<FanArtSubmissionsRequest>;

/**
 * Build a FanArtSubmissions update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateFanArtSubmissions(submissionId: string, data: FanArtSubmissionsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `fanArtSubmissions/${submissionId}`, document: doc };
}

/**
 * Only admin, founder, or PROJECT_VAULT_ADDRESS can update. Used for changing status from 'pending' to 'approved' or 'rejected', and setting reviewedAt timestamp. Immutable fields (artistName, xLink, xProfile, imageUrl, submittedAt) cannot be changed. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateFanArtSubmissions(submissionId: string, data: FanArtSubmissionsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`fanArtSubmissions/${submissionId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating FanArtSubmissions: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Approved submissions are publicly readable by anyone. Admin, founder, and PROJECT_VAULT_ADDRESS can read all submissions regardless of status (for review/moderation dashboards).
   (Get Single Item)
 */
export async function getFanArtSubmissions(submissionId: string): Promise<FanArtSubmissionsResponse | null> {
  try {
    const result = await get(`fanArtSubmissions/${submissionId}`);
    return result as FanArtSubmissionsResponse | null;
  } catch (error) {
    console.error(`Error getting FanArtSubmissions: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single FanArtSubmissions document. (
  Read Operation Details: Approved submissions are publicly readable by anyone. Admin, founder, and PROJECT_VAULT_ADDRESS can read all submissions regardless of status (for review/moderation dashboards).
  )
 */
export function subscribeFanArtSubmissions(callback: (data: FanArtSubmissionsResponse | null) => void, submissionId: string): Promise<() => Promise<void>> {
  return subscribe(`fanArtSubmissions/${submissionId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as FanArtSubmissionsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to FanArtSubmissions at path '${`fanArtSubmissions/${submissionId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many FanArtSubmissions items from collection fanArtSubmissions
 
  Read Operation Details: Approved submissions are publicly readable by anyone. Admin, founder, and PROJECT_VAULT_ADDRESS can read all submissions regardless of status (for review/moderation dashboards).
  
 */
export async function getManyFanArtSubmissions(filter: string = ""): Promise<FanArtSubmissionsResponse[]> {
  try {
    const results = await get("fanArtSubmissions", { prompt: filter });
    return Array.isArray(results) ? results as FanArtSubmissionsResponse[] : [];
  } catch (error) {
    console.error(`Error getting FanArtSubmissions collection at path 'fanArtSubmissions': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in FanArtSubmissions collection at fanArtSubmissions
 
  Read Operation Details: Approved submissions are publicly readable by anyone. Admin, founder, and PROJECT_VAULT_ADDRESS can read all submissions regardless of status (for review/moderation dashboards).
  
 */
export function subscribeManyFanArtSubmissions(callback: (data: FanArtSubmissionsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("fanArtSubmissions", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as FanArtSubmissionsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to FanArtSubmissions collection at path '${"fanArtSubmissions"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all FanArtSubmissions items from collection fanArtSubmissions
 
  Read Operation Details: Approved submissions are publicly readable by anyone. Admin, founder, and PROJECT_VAULT_ADDRESS can read all submissions regardless of status (for review/moderation dashboards).
  
 */
export async function getAllFanArtSubmissions(filter: string = ""): Promise<FanArtSubmissionsResponse[]> {
  try {
    const results = await get("fanArtSubmissions", { prompt: filter });
    return Array.isArray(results) ? results as FanArtSubmissionsResponse[] : [];
  } catch (error) {
    console.error(`Error getting FanArtSubmissions collection at path 'fanArtSubmissions': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in FanArtSubmissions collection at fanArtSubmissions
 
  Read Operation Details: Approved submissions are publicly readable by anyone. Admin, founder, and PROJECT_VAULT_ADDRESS can read all submissions regardless of status (for review/moderation dashboards).
  
 */
export function subscribeAllFanArtSubmissions(callback: (data: FanArtSubmissionsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("fanArtSubmissions", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as FanArtSubmissionsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to FanArtSubmissions collection at path '${"fanArtSubmissions"}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Only admin or founder can delete submissions for moderation or cleanup purposes.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteFanArtSubmissions(submissionId: string): Promise<boolean> {
  try {
    const succeeded = await set(`fanArtSubmissions/${submissionId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting FanArtSubmissions: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for FanArtSubmissions for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteFanArtSubmissions(submissionId: string): DocumentOperation {
  return { path: `fanArtSubmissions/${submissionId}`, document: null };
}

